import type {
	OrganizationServiceAcceptMemberInvitationData,
	OrganizationServiceAcceptOnboardInvitationData,
	OrganizationServiceCreateOrganizationSecretsData,
	OrganizationServiceDeleteOrganizationData,
	OrganizationServiceDeleteOrganizationMemberData,
	OrganizationServiceDeleteOrganizationSecretData,
	OrganizationServiceGetOrganizationAppearanceData,
	OrganizationServiceGetOrganizationListData,
	OrganizationServiceGetOrganizationMemberListData,
	OrganizationServiceGetOrganizationWhiteLabelDetailsByDomainData,
	OrganizationServiceInviteOrganizationMembersData,
	OrganizationServiceToggleOrganizationMfaData,
	OrganizationServiceUpdateOrganizationAppearanceData,
	OrganizationServiceUpdateOrganizationData,
	OrganizationServiceUpdateOrganizationMemberData,
	OrganizationServiceUpdateOrganizationMemberMfaData,
	OrganizationServiceUpdateOrganizationMemberRoleData,
	OrganizationServiceUpdateOrganizationRolesData,
	OrganizationServiceUpdateOrganizationSecretData,
	v1CreateOrganizationRequest,
	v1GetOrganizationRoleListResponse,
	v1WhiteLabelDetails,
} from '@clients/api/organization';
import { OrganizationServiceService } from '@clients/api/organization';
import filter from 'lodash/filter';
import find from 'lodash/find';
import { action, autorun, makeObservable, observable, runInAction } from 'mobx';

import { Organization } from './Organization';
import type { IOrganizationStore, v1Organization } from './types';

export class OrganizationStore implements Partial<IOrganizationStore> {
	@observable current?: Organization | null;
	@observable data: Organization[] = [];
	@observable totalCount: number = 0;

	@observable whiteLabel?: v1WhiteLabelDetails | null;

	constructor() {
		makeObservable(this);

		// Log when current organization is changed
		autorun(() => {
			console.debug(
				`Detected organization current is changed! ${JSON.stringify({ id: this.current?.id, name: this.current?.name }, null, 2)}`
			);
		});
	}

	@action
	async fetch(
		args: OrganizationServiceGetOrganizationListData = {}
	): Promise<Organization[]> {
		const response =
			await OrganizationServiceService.organizationServiceGetOrganizationList(
				args
			);
		const { data, totalCount } = response;
		runInAction(() => {
			this.totalCount = totalCount || 0;

			this.data = (data || [])?.map(
				(el) => new Organization({ ...el, store: this })
			);
		});
		return this.data;
	}

	@action
	async fetchCurrent(id?: string) {
		const current =
			await OrganizationServiceService.organizationServiceGetOrganization({
				id: id as string,
			});

		const appearance = await this.fetchAppearanceConfig({
			orgId: id as string,
		});

		runInAction(() => {
			this.current = new Organization({ ...current, appearance, store: this });
		});
		return current;
	}

	@action
	async fetchOrganizationRoles(orgId: string) {
		const results =
			await OrganizationServiceService.organizationServiceGetOrganizationRoleList(
				{
					orgId,
				}
			);
		return results;
	}

	@action
	async fetchPermissionTemplatesByOrgId(orgId: string) {
		const { permissionTemplates } =
			await OrganizationServiceService.organizationServiceGetOrganizationPermissionTemplates(
				{
					orgId,
				}
			);
		// const { roleList } = orgRoles;
		return permissionTemplates;
	}

	@action
	async updateOrganizationPermissionRoles(
		orgId: string,
		requestBody: OrganizationServiceUpdateOrganizationRolesData['requestBody']
	) {
		const { roleList } =
			(await OrganizationServiceService.organizationServiceUpdateOrganizationRoles(
				{
					orgId,
					requestBody,
				}
			)) as v1GetOrganizationRoleListResponse;
		// const { roleList } = orgRoles;
		return roleList;
	}

	getOne(filterProps: Partial<v1Organization>) {
		return find<v1Organization>(this.data, filterProps);
	}

	getMany(filterProps: Partial<v1Organization>) {
		return filter<v1Organization>(this.data, filterProps);
	}

	@action
	switchByIdx(idx: number) {
		const current = this.data?.[idx];
		if (current) this.current = current;
	}

	@action
	switchById(id: string) {
		const current = find(this.data, { id });
		if (current) this.current = current;
	}

	@action
	async createOrganization(data: v1CreateOrganizationRequest) {
		const response =
			await OrganizationServiceService.organizationServiceCreateOrganization({
				requestBody: data,
			});
		await this.fetch(); // Refresh the list after creation
		return response;
	}

	@action
	async updateOrganization(
		data: OrganizationServiceUpdateOrganizationData,
		isOnboarding?: boolean
	) {
		const response =
			await OrganizationServiceService.organizationServiceUpdateOrganization(
				data
			);

		if (!isOnboarding) {
			await this.fetch(); // Refresh the list after update
		}

		return response;
	}

	@action
	async deleteOrganization(data: OrganizationServiceDeleteOrganizationData) {
		const response =
			await OrganizationServiceService.organizationServiceDeleteOrganization(
				data
			);
		await this.fetch(); // Refresh the list after deletion
		return response;
	}

	@action
	async fetchMembers(data: OrganizationServiceGetOrganizationMemberListData) {
		const response =
			await OrganizationServiceService.organizationServiceGetOrganizationMemberList(
				data
			);

		return response;
	}

	@action
	async inviteMembers(data: OrganizationServiceInviteOrganizationMembersData) {
		const response =
			await OrganizationServiceService.organizationServiceInviteOrganizationMembers(
				data
			);
		await this.fetchMembers({ orgId: data.orgId });
		return response;
	}

	@action
	async updateMember(data: OrganizationServiceUpdateOrganizationMemberData) {
		const response =
			await OrganizationServiceService.organizationServiceUpdateOrganizationMember(
				data
			);
		return response;
	}

	@action
	async bulkUpdateMemberMfa(
		data: OrganizationServiceUpdateOrganizationMemberMfaData
	) {
		const response =
			await OrganizationServiceService.organizationServiceUpdateOrganizationMemberMfa(
				data
			);
		return response;
	}

	@action
	// this is a standalone endpoint for update member role only, but user only required update_member_role_permission, no need update_member_permission
	async updateMemberRole(
		data: OrganizationServiceUpdateOrganizationMemberRoleData
	) {
		const response =
			await OrganizationServiceService.organizationServiceUpdateOrganizationMemberRole(
				data
			);
		return response;
	}

	@action
	async deleteMember(data: OrganizationServiceDeleteOrganizationMemberData) {
		const response =
			await OrganizationServiceService.organizationServiceDeleteOrganizationMember(
				data
			);
		return response;
	}

	@action
	async createSecret(data: OrganizationServiceCreateOrganizationSecretsData) {
		const response =
			await OrganizationServiceService.organizationServiceCreateOrganizationSecrets(
				data
			);
		return response;
	}

	@action
	async updateSecret(data: OrganizationServiceUpdateOrganizationSecretData) {
		const response =
			await OrganizationServiceService.organizationServiceUpdateOrganizationSecret(
				data
			);
		return response;
	}

	@action
	async deleteSecret(data: OrganizationServiceDeleteOrganizationSecretData) {
		const response =
			await OrganizationServiceService.organizationServiceDeleteOrganizationSecret(
				data
			);
		return response;
	}

	@action
	async acceptInvitation(data: OrganizationServiceAcceptOnboardInvitationData) {
		const response =
			await OrganizationServiceService.organizationServiceAcceptOnboardInvitation(
				data
			);
		return response;
	}

	@action
	async acceptMemberInvitation(
		data: OrganizationServiceAcceptMemberInvitationData
	) {
		const response =
			await OrganizationServiceService.organizationServiceAcceptMemberInvitation(
				data
			);
		return response;
	}

	@action
	async getWhiteLabel(
		data: OrganizationServiceGetOrganizationWhiteLabelDetailsByDomainData
	) {
		const response =
			await OrganizationServiceService.organizationServiceGetOrganizationWhiteLabelDetailsByDomain(
				data
			);

		this.whiteLabel = response;
		return response;
	}

	@action
	async fetchAppearanceConfig(
		data: OrganizationServiceGetOrganizationAppearanceData
	) {
		const response =
			await OrganizationServiceService.organizationServiceGetOrganizationAppearance(
				data
			);

		return response;
	}

	@action
	async updateAppearanceConfig(
		orgId: string,
		data: OrganizationServiceUpdateOrganizationAppearanceData['requestBody']
	) {
		const response =
			await OrganizationServiceService.organizationServiceUpdateOrganizationAppearance(
				{
					orgId,
					requestBody: data,
				}
			);

		return response;
	}

	@action
	async updateMfa(data: OrganizationServiceToggleOrganizationMfaData) {
		const response =
			await OrganizationServiceService.organizationServiceToggleOrganizationMfa(
				data
			);

		return response;
	}
}

export const organizationStore = new OrganizationStore();
